import { useCallback, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import { useHoverPopoverContent } from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import { useHoverPopoverSetter } from '@atlassian/jira-hover-popover/src/controllers/hover-popover-state-provider/index.tsx';

const HOVER_DEBOUNCE_DURATION = 100;

export const useEventDelegator = () => {
	const { setPopoverClosed, setPopoverOpen } = useHoverPopoverSetter();
	const { getCellIdByRef } = useHoverPopoverContent();

	const currentHoveredCellRelayIdRef = useRef<string | null>(null);
	const debouncedSetHoverFocusStateRef = useRef<ReturnType<typeof debounce> | null>(null);

	const clearHoverFocusState = useCallback(() => {
		debouncedSetHoverFocusStateRef.current?.cancel();
		setPopoverClosed();
		currentHoveredCellRelayIdRef.current = null;
	}, [setPopoverClosed]);

	const setHoverFocusState = useCallback(
		(relayFieldId: string, cell: HTMLTableCellElement) => {
			setPopoverOpen({
				relayFieldId,
				cell,
				clearHoverFocusState,
			});
		},
		[setPopoverOpen, clearHoverFocusState],
	);

	const handleMouseOver = useCallback(
		(event: React.MouseEvent<HTMLTableElement>) => {
			if (event.target instanceof HTMLElement) {
				const target = event.target;
				// Get closest cell that is being hovered over
				const cell = target.closest('td');
				// If no cell is found, clear the hover state and return
				if (!cell || !(cell instanceof HTMLTableCellElement)) {
					clearHoverFocusState();
					return;
				}
				const relayFieldId = getCellIdByRef(cell);

				// If there is a cell but no relay field id, clear the hover state and return
				if (!relayFieldId) {
					clearHoverFocusState();
					return;
				}

				// If the cell is the same as the current hovered cell, return
				if (relayFieldId !== currentHoveredCellRelayIdRef.current) {
					// If the cell is different from the current hovered cell, clear the previous hover state
					if (currentHoveredCellRelayIdRef.current) {
						clearHoverFocusState();
					}

					// Finally, set the hover state for the new cell
					currentHoveredCellRelayIdRef.current = relayFieldId;
					debouncedSetHoverFocusStateRef.current = debounce(
						() => setHoverFocusState(relayFieldId, cell),
						HOVER_DEBOUNCE_DURATION,
					);
					debouncedSetHoverFocusStateRef.current();
				}
			}
		},
		[clearHoverFocusState, getCellIdByRef, setHoverFocusState],
	);

	const handleMouseLeave = useCallback(() => {
		clearHoverFocusState();
		debouncedSetHoverFocusStateRef.current?.cancel();
	}, [clearHoverFocusState]);

	useEffect(() => {
		return () => {
			if (currentHoveredCellRelayIdRef.current) {
				clearHoverFocusState();
			}
			debouncedSetHoverFocusStateRef.current?.cancel();
		};
	}, [clearHoverFocusState]);

	return {
		handleMouseLeave,
		handleMouseOver,
	};
};

// Returns a ref to bind to the table cell.
// It adds the table cell element and its corresponding Relay field ID to the cellRefRelayMap.
// lastNode ensures that a cell's ref/relay field ID is removed from the map when the cell is unmounted.
// This is used by the event listener to look up the corresponding relay field ID for the cell.
export const useEventDelegatorRef = (relayFieldId?: string) => {
	const lastNode = useRef<HTMLTableCellElement | null>(null);
	const { setCellIdByRef, deleteCellIdByRef } = useHoverPopoverContent();

	const tableCellRef = useCallback(
		(node: HTMLTableCellElement | null) => {
			if (node && relayFieldId) {
				setCellIdByRef(node, relayFieldId);
				lastNode.current = node;
			} else if (lastNode.current) {
				deleteCellIdByRef(lastNode.current);
				lastNode.current = null;
			}
		},
		[relayFieldId, setCellIdByRef, deleteCellIdByRef],
	);

	return tableCellRef;
};
